import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getDocs,
  getDoc,
  collection,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase-client';
import SubmitButton from '../formComponents/SubmitButton';
import { createNotification } from '../../../utils/createNotification';
import { getNextWaitlistPriority, isWaitlistJoinRoutingEnabled } from '../../../utils/waitlist';

function normalizeTargetDetails(member) {
  const fromPayload = Array.isArray(member?.membershipTargetDetails)
    ? member.membershipTargetDetails
    : [];

  if (fromPayload.length > 0) {
    return fromPayload
      .map((target) => {
        const uid = typeof target?.uid === 'string' ? target.uid.trim() : '';
        if (!uid) return null;
        return {
          uid,
          displayName:
            typeof target?.displayName === 'string' && target.displayName.trim()
              ? target.displayName.trim()
              : 'Family member',
          role: typeof target?.role === 'string' && target.role.trim() ? target.role.trim() : null,
          isPayer: target?.isPayer === true,
          formResponses: Array.isArray(target?.formResponses) ? target.formResponses : [],
          appliedTags: Array.isArray(target?.appliedTags) ? target.appliedTags : [],
          requestedTags: Array.isArray(target?.requestedTags) ? target.requestedTags : [],
        };
      })
      .filter(Boolean);
  }

  return [
    {
      uid: member?.id || '',
      displayName: member?.displayName || 'Unnamed',
      role: null,
      isPayer: true,
      formResponses: Array.isArray(member?.formResponses) ? member.formResponses : [],
      appliedTags: Array.isArray(member?.appliedTags) ? member.appliedTags : [],
      requestedTags: Array.isArray(member?.requestedTags) ? member.requestedTags : [],
    },
  ].filter((target) => Boolean(target.uid));
}

export default function EditPendingMembersForm({ isOpen, teamId, onClose, allTags, teamName, onSave, canManageFinance = false }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [waitlistJoinRoutingEnabled, setWaitlistJoinRoutingEnabled] = useState(false);
  const [reconciliationEnabled, setReconciliationEnabled] = useState(false);

  const groupedTags = (Array.isArray(allTags) ? allTags : []).reduce((acc, tag) => {
    if (tag?.type === 'ageRule') return acc;
    const category = tag?.category || 'Uncategorised';
    if (!acc[category]) acc[category] = [];
    acc[category].push(tag);
    return acc;
  }, {});

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setError(null);

    const pendingApprovalQuery = query(
      collection(db, 'teams', teamId, 'pendingMembers'),
      where('status', 'in', ['waiting', 'awaiting_club_payment', 'awaiting_payment_round'])
    );

    Promise.all([
      getDocs(pendingApprovalQuery),
      getDoc(doc(db, 'teams', teamId)),
    ])
      .then(([snap, teamSnap]) => {
        const requests = snap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const request = { id: docSnap.id, ...data };
          const targets = normalizeTargetDetails(request);
          const selectedTagsByUid = targets.reduce((acc, target) => {
            acc[target.uid] = Array.from(new Set([
              ...(Array.isArray(target.appliedTags) ? target.appliedTags : []),
              ...(Array.isArray(target.requestedTags) ? target.requestedTags : []),
            ]));
            return acc;
          }, {});

          return {
            ...request,
            targets,
            selectedTagsByUid,
          };
        });
        setPending(requests);
        setWaitlistJoinRoutingEnabled(isWaitlistJoinRoutingEnabled(teamSnap?.data() || {}));
        setReconciliationEnabled(teamSnap?.data()?.modules?.membershipReconciliation?.enabled === true);
      })
      .catch((err) => setError(err.message || 'Failed to load pending members.'))
      .finally(() => setLoading(false));
  }, [isOpen, teamId]);

  const updateTargetTags = (memberId, targetUid, categoryTagNames, selectedForCategory) => {
    setPending((prev) => prev.map((member) => {
      if (member.id !== memberId) return member;
      const current = Array.isArray(member?.selectedTagsByUid?.[targetUid])
        ? member.selectedTagsByUid[targetUid]
        : [];
      const otherSelected = current.filter((tag) => !categoryTagNames.includes(tag));
      const nextTags = Array.from(new Set([...otherSelected, ...selectedForCategory]));

      return {
        ...member,
        selectedTagsByUid: {
          ...(member.selectedTagsByUid || {}),
          [targetUid]: nextTags,
        },
      };
    }));
  };

  const handleApprove = async (memberId) => {
    const request = pending.find((entry) => entry.id === memberId);
    if (!request) return;

    if (request.status === 'awaiting_club_payment') {
      try {
        const admit = httpsCallable(functions, 'admitClubManagedApplicant');
        await admit({ teamId, payerUid: memberId });
        setPending((current) => current.filter((entry) => entry.id !== memberId));
        await onSave?.();
      } catch (err) {
        setError(err?.message || 'Could not admit this member.');
      }
      return;
    }

    const approvedTargets = (Array.isArray(request.targets) ? request.targets : []).map((target) => ({
      ...target,
      appliedTags: Array.isArray(request?.selectedTagsByUid?.[target.uid])
        ? request.selectedTagsByUid[target.uid]
        : [],
      requestedTags: [],
    }));

    const payerTarget = approvedTargets.find((target) => target.uid === memberId)
      || approvedTargets.find((target) => target.isPayer)
      || approvedTargets[0]
      || { formResponses: [], appliedTags: [] };

    const nextWaitlistPriority = waitlistJoinRoutingEnabled
      ? await getNextWaitlistPriority(teamId)
      : null;

    await updateDoc(doc(db, 'teams', teamId, 'pendingMembers', memberId), {
      status: waitlistJoinRoutingEnabled ? 'waitlisted' : 'approved',
      approvedAt: serverTimestamp(),
      waitlistPriority: waitlistJoinRoutingEnabled ? nextWaitlistPriority : null,
      waitlistedAt: waitlistJoinRoutingEnabled ? serverTimestamp() : null,
      releasedFromWaitlistAt: null,
      appliedTags: Array.isArray(payerTarget.appliedTags) ? payerTarget.appliedTags : [],
      requestedTags: [],
      formResponses: Array.isArray(payerTarget.formResponses) ? payerTarget.formResponses : [],
      membershipTargetDetails: approvedTargets,
    });

    await createNotification({
      userId: memberId,
      message: waitlistJoinRoutingEnabled
        ? `You've been added to the waitlist for "${teamName}".`
        : `You've been approved to join "${teamName}". Click to continue.`,
      actionUrl: `/join/${teamId}`,
      teamId,
      notificationCategory: waitlistJoinRoutingEnabled ? 'events' : 'admin',
      pushContext: teamName,
    });

    setPending((prev) => prev.filter((entry) => entry.id !== memberId));
    if (typeof onSave === 'function') onSave();
  };

  const handleReject = async (memberId) => {
    const member = pending.find((entry) => entry.id === memberId);
    const label = member?.displayName || 'this applicant';
    if (!window.confirm(`Reject ${label} and permanently delete their application?`)) return;
    await deleteDoc(doc(db, 'teams', teamId, 'pendingMembers', memberId));
    setPending((prev) => prev.filter((entry) => entry.id !== memberId));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="modal-content">
        <h2 className="modal-title">Review Pending Members</h2>
        {error && <p className="text-error">{error}</p>}
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className="modal-form space-y-6">
            {pending.map((member) => {
              const requestLabel = member.displayName || 'Unnamed';
              const targetCount = Array.isArray(member.targets) ? member.targets.length : 0;

              return (
                <div key={member.id} className="pending-entry border-b pb-4 mb-4">
                  <h4>{requestLabel}</h4>
                  {member.status === 'awaiting_club_payment' && <p className="text-muted">
                    Offline payment awaiting confirmation. {member.selectedCadence?.unit === 'month' ? 'Monthly membership.' : member.selectedCadence?.unit === 'year' ? 'Yearly membership.' : ''}{' '}
                    {reconciliationEnabled ? 'Record this membership payment as paid or waived in payment reconciliation to activate access.' : 'Admit the member after confirming their payment with the club.'}
                  </p>}
                  {targetCount > 1 && (
                    <p className="text-muted" style={{ marginBottom: '0.5rem' }}>
                      Family request for {targetCount} members. Approve once to approve the full request.
                    </p>
                  )}

                  {(Array.isArray(member.targets) ? member.targets : []).map((target) => {
                    const selectedForTarget = Array.isArray(member?.selectedTagsByUid?.[target.uid])
                      ? member.selectedTagsByUid[target.uid]
                      : [];

                    return (
                      <div
                        key={`${member.id}-${target.uid}`}
                        style={{
                          border: targetCount > 1 ? '1px solid #e5e7eb' : 'none',
                          borderRadius: targetCount > 1 ? '8px' : 0,
                          padding: targetCount > 1 ? '0.75rem' : 0,
                          marginTop: '0.5rem',
                        }}
                      >
                        {targetCount > 1 && (
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>{target.displayName || 'Family member'}</strong>
                            <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                              {target.isPayer ? 'Payer' : (target.role || 'Member')}
                            </div>
                          </div>
                        )}

                        <ul className="mb-2">
                          {(Array.isArray(target.formResponses) ? target.formResponses : []).map((response) => (
                            <li key={`${target.uid}-${response.id}`}>
                              <strong>{response.label}:</strong> {String(response.value ?? '')}
                            </li>
                          ))}
                        </ul>

                        {Array.isArray(target.requestedTags) && target.requestedTags.length > 0 && (
                          <p className="mb-2">
                            <strong>Originally Requested Tags:</strong> {target.requestedTags.join(', ')}
                          </p>
                        )}

                        {Object.entries(groupedTags).map(([category, tags]) => {
                          const categoryTagNames = tags.map((tag) => tag.name).filter(Boolean);
                          const selectedInCategory = selectedForTarget.filter((tagName) =>
                            categoryTagNames.includes(tagName)
                          );

                          return (
                            <div
                              className="form-group"
                              key={`${target.uid}-${category}`}
                              style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '0.65rem 0.75rem',
                                marginBottom: '0.5rem',
                                background: '#f9fafb',
                              }}
                            >
                              <label style={{ fontWeight: 700, display: 'block', marginBottom: '0.35rem' }}>
                                {category}
                              </label>
                              <div style={{ display: 'grid', gap: '0.35rem', marginTop: '0.25rem' }}>
                                {tags.map((tag) => {
                                  const tagName = tag?.name;
                                  if (!tagName) return null;
                                  const checked = selectedInCategory.includes(tagName);

                                  return (
                                    <label
                                      key={tagName}
                                      style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => {
                                          const next = e.target.checked
                                            ? Array.from(new Set([...selectedInCategory, tagName]))
                                            : selectedInCategory.filter((name) => name !== tagName);
                                          updateTargetTags(member.id, target.uid, categoryTagNames, next);
                                        }}
                                      />
                                      <span>{tagName}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}

                  <div className="form-actions mt-2">
                    {member.status === 'awaiting_club_payment' && reconciliationEnabled ? (
                      canManageFinance ? <Link className="button button-primary" to={`/teams/${teamId}/membership-reconciliation`}>Open payment reconciliation</Link>
                        : <p className="text-muted">Ask an administrator with finance permission to record the payment.</p>
                    ) : <SubmitButton
                      label={member.status === 'awaiting_club_payment' ? 'Confirm payment and admit' : member.status === 'awaiting_payment_round' ? 'Awaiting round assignment' : 'Approve'}
                      onClick={() => handleApprove(member.id)}
                      variant="primary"
                      disabled={member.status === 'awaiting_payment_round'}
                    />}
                    <SubmitButton
                      label="Reject"
                      onClick={() => handleReject(member.id)}
                      variant="danger"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="form-actions mt-4">
          <SubmitButton label="Close" onClick={onClose} />
        </div>
      </div>
    </div>
  );
}
